import { Input, Select, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import UpdateAnyStatus from "../../../components/admin/Forum/anyStatus/UpdateAnyStatus";
import FilterSection from "../../../components/common/FilterSection";
import ActionButton from "../../../components/ui/ActionButton";
import CustomTable from "../../../components/ui/CustomTable";
import useDebounce from "../../../hooks/useDebounce";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import AdminLayout from "../../../layouts/AdminLayout";
import { formatDate, formatTime } from "../../../lib/time";
import {
  deleteCampaignAsync,
  deleteEventAsync,
  deleteWebinarAsync,
  getAllPendingRequestAsync,
} from "../../../store/features/EventManage/PendingRequest/pendingRequestApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { TableData } from "../../../types";
import { PendingRequestData } from "../../../types/eventManage/pendingRequest";
import { countRow } from "../../../utils/number";
import { addKeyInArray, changeStatus, skipData } from "../../../utils/utils";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import DeleteConfirmModal from "../../../components/admin/Forum/anyStatus/DeleteConfirmModal";

type Event = "webinar" | "event";

type Status =
  | "Published"
  | "CSO-Admin-Approved"
  | "Cancel"
  | "Completed"
  | "Pending";

const dealerStatusClass: Record<Status, string> = {
  Published: "status-waiting",
  "CSO-Admin-Approved": "status-waiting",
  Cancel: "status-danger",
  Completed: "status-done",
  Pending: "status-pending",
};

const changeStatused = (status: Status) => {
  // Optional formatter (e.g., replace hyphens with spaces and capitalize)
  return status.replace(/-/g, " ");
};

const eventTypeClass = {
  webinar: "text-[#6230F6]",
  event: "text-[#D638EE]",
};

const eventTypeData = ["event", "webinar", "campaign"];

export default function PendingRequests() {
  const dispatch = useAppDispatch();
  const { keyword, handleInputChange } = useDebounce();
  const { open, toggle, handleOpen } = useModal(reRender);
  const { limit, page, handlePagination, handleLimit, resetPagination } =
    useTable();
  const { PendingRequestList, loading, countPendingRequest } = useAppSelector(
    (state) => state.pendingRequest
  );

  const [eventTypeFilter, setEventTypeFilter] = useState<
    "event" | "webinar" | "campaign" | undefined
  >(undefined);
  const [searchText, setSearchText] = useState("");

  const columns: ColumnsType<TableData<PendingRequestData>> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, idx) => countRow(page, limit, idx),
    },
    {
      title: "Webinars Title",
      dataIndex: "title",
      render: (val) => <span>{val || "N/A"}</span>,
    },

    {
      title: "Event Type",
      dataIndex: "type",
      render: (val: Event) => (
        <span className={eventTypeClass[val]}>{val}</span>
      ),
    },

    {
      title: "start Date",
      dataIndex: "startDate",
      render: (val) => (
        <div>
          <p>{formatDate(val)}</p>
          <p>{formatTime(val)}</p>
        </div>
      ),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      render: (val) => (
        <div>
          <p>{formatDate(val)}</p>
          <p>{formatTime(val)}</p>
        </div>
      ),
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "status",
      render: (val: Status) => (
        <span className={dealerStatusClass[val]}>{changeStatused(val)}</span>
      ),
    },

    {
      title: "Action",
      align: "center",
      width: 100,
      render: (val) => (
        <Space size="small">
          <ActionButton
            icon={<EditIcon />}
            onClick={() =>
              handleOpen({
                type: "EDIT",
                data: val,
              })
            }
          />

          <ActionButton
            icon={<DeleteIcon />}
            onClick={() =>
              handleOpen({
                type: "DELETE",
                data: val,
              })
            }
          />
        </Space>
      ),
    },
  ];

  const getData = useCallback(() => {
    dispatch(
      getAllPendingRequestAsync({
        params: {
          skip: skipData(page, limit),
          limit,
          custom_text: keyword,
          userId: "",
        },
      })
    );
  }, [dispatch, keyword, limit, page]);

  // Handle delete action
  const handleDelete = useCallback(() => {
    if (open?.data?.type === "event") {
      dispatch(
        deleteEventAsync({
          data: { id: open.data.id },
          others: { reRender },
        })
      );
    } else if (open?.data?.type === "webinar") {
      dispatch(
        deleteWebinarAsync({
          data: { id: open.data.id },
          others: { reRender },
        })
      );
    } else if (open?.data?.type === "campaign") {
      dispatch(
        deleteCampaignAsync({
          data: { id: open.data.id },
          others: { reRender },
        })
      );
    }
  }, [open?.data, dispatch, reRender]);

  useEffect(() => {
    getData();
  }, [getData]);

  function reRender() {
    getData();
    toggle();
  }

  return (
    <AdminLayout
      title="Pending Requests"
      className="dashboard"
      // headerChildren={
      //   <Link to={`/admin/event-manage/webinars/add-webinars`}>
      //     <Button
      //       className="primary_btn"
      //       type="primary"
      //       size="large"
      //       icon={<AddIcon />}
      //       onClick={() =>
      //         handleOpen({
      //           type: "ADD",
      //         })
      //       }
      //     >
      //       Create New Webinars
      //     </Button>
      //   </Link>
      // }
    >
      <CustomTable<TableData<PendingRequestData>>
        loading={loading}
        total={countPendingRequest}
        limit={limit}
        page={page}
        handlePagination={handlePagination}
        handleLimit={handleLimit}
        dataList={addKeyInArray(PendingRequestList)}
        columns={columns}
        bordered={false}
        isLimitRow
        showTotalPage
        tableTopChildren={
          <FilterSection>
            <Input
              size="large"
              suffix={<SearchIcon />}
              placeholder={`Search within ${countPendingRequest} data`}
              onChange={(e) => {
                handleInputChange(e);
                resetPagination();
              }}
            />

            <Select
              value={eventTypeFilter}
              placeholder="Filter by Event Type"
              onChange={(value) => {
                setEventTypeFilter(value);
                dispatch(
                  getAllPendingRequestAsync({
                    params: {
                      skip: skipData(page, limit),
                      limit,
                      custom_text: searchText,
                      event_type: value,
                      userId: "",
                    },
                  })
                );
              }}
              options={[
                { value: "event", label: "Event" },
                { value: "webinar", label: "Webinar" },
                { value: "campaign", label: "Campaign" },
              ]}
              allowClear
              className="min-w-[10rem]"
              suffixIcon={<SelectDownIcon width={14} height={8} />}
            />
          </FilterSection>
        }
      />

      {open?.type === "EDIT" && (
        <UpdateAnyStatus open={open} handleClose={toggle} />
      )}

      {open?.type === "DELETE" && (
        <DeleteConfirmModal
          open={open}
          handleClose={toggle}
          handleDelete={handleDelete}
          loading={loading}
        />
      )}
    </AdminLayout>
  );
}
